<template>
    <div class="container-fluid">
        <div class="row">
            <div class="col-sm-12">
                <div class="card">
                    <div class="card-header pb-0">
                        <h5>Change Siddar</h5>
                    </div>
                    <div class="card-body">
                        <!-- Notification Banner -->
                        <div v-if="notification" class="alert alert-success alert-dismissible fade show" role="alert">
                            {{ notification }}
                            <button type="button" class="btn-close" @click="notification = ''" aria-label="Close"></button>
                        </div>

                        <!-- Error Message -->
                        <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
                            {{ error }}
                            <button type="button" class="btn-close" @click="error = ''" aria-label="Close"></button>
                        </div>

                        <!-- Loading State -->
                        <div v-if="loading" class="text-center my-4">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>

                        <!-- Filters Section -->
                        <div v-if="!loading" class="row mb-4">
                            <!-- Include Vacant Positions Checkbox -->
                            <div class="col-12 mb-3">
                                <div class="form-check">
                                    <input 
                                        type="checkbox" 
                                        class="form-check-input" 
                                        id="includeVacant"
                                        v-model="filters.includeVacant"
                                    >
                                    <label class="form-check-label" for="includeVacant">
                                        Include All Current Vacant Positions Irrespective of Time Period
                                    </label>
                                </div>
                            </div>

                            <!-- Date Range Filters -->
                            <div class="col-md-6">
                                <div class="row">
                                    <div class="col-md-6">
                                        <label class="form-label">From Date</label>
                                        <input 
                                            type="date" 
                                            class="form-control" 
                                            v-model="filters.dateFrom"
                                        >
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label">To Date</label>
                                        <input 
                                            type="date" 
                                            class="form-control" 
                                            v-model="filters.dateTo"
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Table Section -->
                        <div v-if="!loading" class="table-responsive">
                            <table class="table table-bordered table-hover">
                                <thead>
                                    <tr>
                                        <th>Employee Name</th>
                                        <th>Current Site</th>
                                        <th>Department</th>
                                        <th>Designation</th>
                                        <th>Change Option</th>
                                        <th>New Site / Reliever Type</th>
                                        <th>Done By</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="employee in filteredEmployees" :key="employee.id">
                                        <td>{{ employee.name }}</td>
                                        <td>{{ employee.currentSite }}</td>
                                        <td>{{ employee.department }}</td>
                                        <td>{{ employee.designation }}</td>
                                        <td>
                                            <select 
                                                class="form-select" 
                                                v-model="employee.changeOption"
                                                @change="handleChangeOption(employee)"
                                            >
                                                <option value="">Select Option</option>
                                                <option value="vacant_site">Vacant Site</option>
                                                <option value="vacant_reliever">Vacant Reliever</option>
                                                <option value="transfer">Transfer to Another Site</option>
                                            </select>
                                        </td>
                                        <td>
                                            <template v-if="employee.changeOption === 'vacant_site'">
                                                <div v-if="loadingVacantSites" class="text-center">
                                                    <div class="spinner-border spinner-border-sm" role="status">
                                                        <span class="visually-hidden">Loading...</span>
                                                    </div>
                                                </div>
                                                <select v-else class="form-select" v-model="employee.newSite">
                                                    <option value="">Select Vacant Site</option>
                                                    <option v-for="site in vacantSites" 
                                                            :key="site.siteUID" 
                                                            :value="site.siteId">
                                                        {{ site.siteName }} ({{ site.vacancies }} vacancies)
                                                    </option>
                                                </select>
                                            </template>
                                            <template v-else-if="employee.changeOption === 'vacant_reliever'">
                                                <select class="form-select" v-model="employee.newRelieverType">
                                                    <option value="">Select Reliever Type</option>
                                                    <option v-for="type in relieverTypes" 
                                                            :key="type.id" 
                                                            :value="type.id">
                                                        {{ type.name }}
                                                    </option>
                                                </select>
                                            </template>
                                            <template v-else-if="employee.changeOption === 'transfer'">
                                                <select class="form-select" v-model="employee.newSite">
                                                    <option value="">Select New Site</option>
                                                    <option v-for="site in availableSites" 
                                                            :key="site.id" 
                                                            :value="site.id">
                                                        {{ site.name }}
                                                    </option>
                                                </select>
                                            </template>
                                        </td>
                                        <td>{{ employee.manager }}</td>
                                        <td>
                                            <button 
                                                class="btn btn-primary" 
                                                @click="applyChange(employee)"
                                                :disabled="!isChangeValid(employee) || loading"
                                            >
                                                <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status">
                                                    <span class="visually-hidden">Loading...</span>
                                                </span>
                                                {{ loading ? 'Applying...' : 'Apply Change' }}
                                            </button>
                                        </td>
                                    </tr>
                                    <tr v-if="!filteredEmployees.length">
                                        <td colspan="8" class="text-center">No employees found</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import axios from 'axios';

interface SiddarEmployee {
    _id: string;
    siteName: string;
    siteId: string;
    department: string;
    employee: string;
    employeeId: string;
    designation: string;
    gender: string;
    daysWorked: number;
    typeOfLeaving: string;
    type: string;
    vacentType: string;
    reason: string;
    manager: string;
    deadline: string;
    status: string;
    createdAt: string;
    recuitmentDeadline: string;
}

interface VacantSite {
    siteName: string;
    siteUID: string;
    siteId: string;
    vacancies: number;
}

interface Employee {
    id: string;
    employeeId: string;
    siteId: string;
    name: string;
    currentSite: string;
    department: string;
    designation: string;
    changeOption: string;
    newSite: string;
    newRelieverType: string | number;
    createdAt?: string;
    manager: string;
}

interface Site {
    id: string;
    name: string;
}

interface RelieverType {
    id: number;
    name: string;
}

interface TransferResponse {
    success: boolean;
    message: string;
    data?: {
        employeeId: string;
        previousSite: string;
        newSite: string;
        transferDate: string;
    };
}

const baseUrl = process.env.VUE_APP_BASE_URL;

// Initialize toast
const toast = useToast();

// State
const notification = ref('');
const currentUser = ref('');
const filters = ref({
    includeVacant: false,
    dateFrom: '',
    dateTo: ''
});

const employees = ref<Employee[]>([]);
const loading = ref(false);
const error = ref('');

const vacantSites = ref<VacantSite[]>([]);
const loadingVacantSites = ref(false);

const availableSites = ref<Site[]>([
    { id: 'site-1', name: 'Site A' },
    { id: 'site-2', name: 'Site B' },
    { id: 'site-3', name: 'Site C' }
]);

const relieverTypes = ref<RelieverType[]>([
    { id: 1, name: 'General Reliever' },
    { id: 2, name: 'Special Reliever' },
    { id: 3, name: 'Emergency Reliever' }
]);

// Fetch employees data
const fetchEmployees = async () => {
    try {
        loading.value = true;
        error.value = '';
        const response = await axios.get(`${baseUrl}api/siddar/get/change`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        
        // Transform the API response to match our Employee interface
        employees.value = response.data.map((item: SiddarEmployee) => ({
            id: item._id,
            employeeId: item.employeeId,
            siteId: item.siteId,
            name: item.employee,
            currentSite: item.siteName,
            department: item.department,
            designation: item.designation,
            changeOption: '',
            newSite: '',
            newRelieverType: '',
            createdAt: item.createdAt,
            manager: item.manager
        }));

        // Set current user from the first record's manager (assuming it's consistent)
        if (response.data.length > 0) {
            currentUser.value = response.data[0].manager;
        }
    } catch (err) {
        error.value = 'Failed to fetch employees data';
        console.error('Error fetching employees:', err);
    } finally {
        loading.value = false;
    }
};

// Fetch vacant sites
const fetchVacantSites = async () => {
    try {
        loadingVacantSites.value = true;
        const response = await axios.get(`${baseUrl}api/temp/sites/vacant`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        
        if (response.data.success) {
            vacantSites.value = response.data.data;
        } else {
            throw new Error(response.data.message || 'Failed to fetch vacant sites');
        }
    } catch (err) {
        console.error('Error fetching vacant sites:', err);
        toast.error('Failed to fetch vacant sites');
    } finally {
        loadingVacantSites.value = false;
    }
};

// Computed
const filteredEmployees = computed(() => {
    let filtered = [...employees.value];

    if (!filters.value.includeVacant) {
        filtered = filtered.filter(emp => emp.currentSite !== 'Vacant');
    }

    if (filters.value.dateFrom || filters.value.dateTo) {
        filtered = filtered.filter(emp => {
            if (!emp.createdAt) return true;
            
            const empDate = new Date(emp.createdAt);
            const fromDate = filters.value.dateFrom ? new Date(filters.value.dateFrom) : null;
            const toDate = filters.value.dateTo ? new Date(filters.value.dateTo) : null;

            if (fromDate && toDate) {
                return empDate >= fromDate && empDate <= toDate;
            } else if (fromDate) {
                return empDate >= fromDate;
            } else if (toDate) {
                return empDate <= toDate;
            }
            return true;
        });
    }

    return filtered;
});

// Methods
const handleChangeOption = async (employee: Employee) => {
    employee.newSite = '';
    employee.newRelieverType = '';

    if (employee.changeOption === 'vacant_site') {
        await fetchVacantSites();
    }
};

const isChangeValid = (employee: Employee) => {
    if (!employee.changeOption) return false;
    
    if (employee.changeOption === 'vacant_reliever' && !employee.newRelieverType) return false;
    if ((employee.changeOption === 'transfer' || employee.changeOption === 'vacant_site') && !employee.newSite) return false;
    
    return true;
};

const getRelieverTypeName = (id: string | number) => {
    const numericId = typeof id === 'string' ? parseInt(id) : id;
    return relieverTypes.value.find(type => type.id === numericId)?.name || '';
};

const applyChange = async (employee: Employee) => {
    try {
        let message = '';
        
        switch (employee.changeOption) {
            case 'vacant_site':
                loading.value = true;
                const transferResponse = await axios.post<TransferResponse>(
                    `${baseUrl}api/temp/transfer/employee`,
                    {
                        employeeId: employee.employeeId,
                        previousSiteId: employee.siteId,
                        newSiteId: employee.newSite,
                        siddarId: employee.id
                    },
                    {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    }
                );

                if (transferResponse.data.success) {
                    const vacantSite = vacantSites.value.find(site => site.siteId === employee.newSite);
                    message = `${employee.name} has been transferred from ${transferResponse.data.data?.previousSite} to ${transferResponse.data.data?.newSite} by ${employee.manager}`;
                    
                    // Refresh the employee list after successful transfer
                    await fetchEmployees();
                } else {
                    throw new Error(transferResponse.data.message || 'Transfer failed');
                }
                break;

            case 'vacant_reliever':
                message = `${employee.name} has been assigned as ${getRelieverTypeName(employee.newRelieverType)} by ${employee.manager}`;
                break;

            case 'transfer':
                const newSiteName = availableSites.value.find(site => site.id === employee.newSite)?.name;
                message = `${employee.name} has been transferred from ${employee.currentSite} to ${newSiteName} by ${employee.manager}`;
                break;
        }

        // Show notification
        notification.value = message;
        
        // Show toast notification
        toast.success(message, {
            timeout: 30 * 24 * 60 * 60 * 1000 // 30 days in milliseconds
        });

        // Reset the employee's change options
        employee.changeOption = '';
        employee.newSite = '';
        employee.newRelieverType = '';

    } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An error occurred while applying the change';
        toast.error(errorMessage);
        error.value = errorMessage;
    } finally {
        loading.value = false;
    }
};

// Initialize data on component mount
onMounted(() => {
    fetchEmployees();
});
</script>

<style scoped>
.table-responsive {
    margin-top: 1rem;
}

.table th {
    white-space: nowrap;
    background-color: #f8f9fa;
}

.form-check {
    margin-bottom: 1rem;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
    .table {
        display: block;
        width: 100%;
        overflow-x: auto;
    }

    .col-md-6 {
        margin-bottom: 1rem;
    }

    .form-check {
        margin-left: 0;
    }
}
</style>
